<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>

    <admin-breadcrumb
        :items="[{ text: 'Reports', path: { name: 'admin-reports' } }]"
        name="Performance & Alerts"
        :title="pageTitle"
    />

    <admin-panel with-header>
      <template v-slot:header>
        <div class="performance-alerts-header">
          <div class="performance-alerts-header__top mb-3">
            <div class="performance-alerts-tabs">
              <b-button
                  v-for="tab in reportTabs"
                  :key="tab.key"
                  size="sm"
                  class="performance-alerts-tab-btn"
                  :variant="activeTab === tab.key ? 'primary' : 'outline-secondary'"
                  @click.prevent="setActiveTab(tab.key)"
              >
                {{ tab.label }}
              </b-button>
            </div>
          </div>

          <div class="performance-alerts-header__bottom d-flex justify-content-between align-items-start flex-wrap">
            <div class="d-flex flex-wrap align-items-center">
              <template v-if="shouldShowGlobalDateFilters">
                <div class="mr-3 mb-2 mb-md-0">
                  <strong class="text-uppercase">From:</strong> {{ formattedStartDate }}
                  <b-form-datepicker
                      v-model="globalFilters.date_from"
                      class="ml-2"
                      :max="maxDate"
                      button-only
                      button-variant="primary"
                      @input="onStartDateChanged"
                  />
                </div>

                <div class="mr-3 mb-2 mb-md-0">
                  <strong class="text-uppercase">To:</strong> {{ formattedEndDate }}
                  <b-form-datepicker
                      v-model="globalFilters.date_to"
                      class="ml-2"
                      :max="maxDate"
                      button-only
                      button-variant="primary"
                      @input="onEndDateChanged"
                  />
                </div>
              </template>

              <template v-if="activeTab === 'wow_order_volume_change'">
                <div class="mr-3 mb-2 mb-md-0">
                  <strong class="text-uppercase">Accounts:</strong>
                  <b-form-select
                      v-model="filtersByTab.wow_order_volume_change.account_scope"
                      :options="wowAccountScopeOptions"
                      class="d-inline-block ml-2 report-inline-select"
                      @change="onFiltersChanged"
                  />
                </div>

                <div class="mr-3 mb-2 mb-md-0">
                  <strong class="text-uppercase">Alert Threshold:</strong>
                  <b-form-input
                      v-model.number="filtersByTab.wow_order_volume_change.decline_threshold_percent"
                      type="number"
                      min="1"
                      max="100"
                      step="1"
                      class="d-inline-block ml-2 report-inline-input"
                      @change="onFiltersChanged"
                  />
                </div>
              </template>

              <template v-if="activeTab === 'order_source_mix'">
                <div class="mr-3 mb-2 mb-md-0">
                  <strong class="text-uppercase">Min GL Share:</strong>
                  <b-form-input
                      v-model.number="filtersByTab.order_source_mix.min_gl_share"
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      class="d-inline-block ml-2 report-inline-input"
                      @change="onFiltersChanged"
                  />
                </div>
              </template>
            </div>

            <div class="d-flex align-items-center">
              <b-btn
                  variant="primary"
                  class="mr-2"
                  v-b-tooltip.hover
                  title="Export PDF"
                  @click.prevent="downloadPdf"
              >
                <b-icon-file-earmark-pdf/>
              </b-btn>

              <b-btn
                  variant="primary"
                  v-b-tooltip.hover
                  title="Reload Report"
                  @click.prevent="reloadCurrentReport"
              >
                <b-icon-arrow-clockwise/>
              </b-btn>
            </div>
          </div>
        </div>
      </template>

      <div class="performance-alerts-summary mb-3">
        <div class="text-muted">
          {{ currentReportConfig.description || 'Report overview' }}
        </div>
      </div>

      <div
          v-if="currentAlerts.length"
          class="performance-alerts-alerts mb-3"
      >
        <div
            v-for="alert in currentAlerts"
            :key="alert.id || `${alert.title}-${alert.message}`"
            class="performance-alerts-alert"
            :class="getAlertClass(alert)"
        >
          <div class="performance-alerts-alert__title">
            {{ alert.title || 'Alert' }}
          </div>

          <div class="performance-alerts-alert__message">
            {{ alert.message || '-' }}
          </div>

          <div
              v-if="alert.threshold || alert.value || alert.account_name || alert.seller_name"
              class="performance-alerts-alert__meta"
          >
            <span
                v-if="alert.account_name || alert.seller_name"
                class="mr-3"
            >
              <strong>Account:</strong> {{ alert.account_name || alert.seller_name }}
            </span>

            <span
                v-if="typeof alert.value !== 'undefined' && alert.value !== null"
                class="mr-3"
            >
              <strong>Value:</strong> {{ formatAlertValue(alert) }}
            </span>

            <span v-if="alert.threshold">
              <strong>Threshold:</strong> {{ alert.threshold }}
            </span>
          </div>
        </div>
      </div>

      <b-table
          :fields="currentReportColumns"
          :items="currentReportRows"
          table-class="text-nowrap"
          responsive
          hover
          show-empty
          empty-text="No data found"
      >
        <template
            v-for="column in sortableHeaderColumns"
            #[`head(${column.key})`]
        >
          <button
              :key="`sort-${column.key}`"
              type="button"
              class="report-sort-head btn btn-link p-0 d-inline-flex align-items-center"
              @click="toggleSort(column.key)"
          >
            <span>{{ column.label }}</span>
            <span class="ml-2">{{ sortIcon(column.key) }}</span>
          </button>
        </template>

        <template #cell(current_period_value)="data">
          {{ formatCurrency(data.item.current_period_value) }}
        </template>

        <template #cell(prior_period_value)="data">
          {{ formatCurrency(data.item.prior_period_value) }}
        </template>

        <template #cell(account_type)="data">
          <span class="text-capitalize">
            {{ data.item.account_type || '-' }}
          </span>
        </template>

        <template #cell(approved_at)="data">
          {{ formatDate(data.item.approved_at) }}
        </template>

        <template #cell(change_percent)="data">
          <span
              class="trajectory-badge"
              :class="getChangeClass(data.item.change_percent, data.item.decline_threshold_percent)"
          >
            {{ formatPercent(data.item.change_percent) }}
          </span>
        </template>

        <template #cell(severity)="data">
          <span
              class="wow-status-badge"
              :class="getWowSeverityClass(data.item.severity)"
          >
            {{ getWowSeverityLabel(data.item.severity) }}
          </span>
        </template>

        <template #cell(percent_orders)="data">
          <span v-if="isGlShareBelowThreshold(data.item)">
            🔴 Critical ({{ formatPlainPercent(data.item.percent_orders) }})
          </span>
          <span v-else>
            {{ formatPlainPercent(data.item.percent_orders) }}
          </span>
        </template>

        <template #cell(coverage_percent)="data">
          <span
              class="coverage-badge"
              :class="getCoverageClass(data.item.coverage_percent)"
          >
            {{ formatPercent(data.item.coverage_percent) }}
          </span>
        </template>

        <template #cell(last_order_date)="data">
          {{ formatDate(data.item.last_order_date) }}
        </template>

        <template #cell(signup_date)="data">
          {{ formatDate(data.item.signup_date) }}
        </template>

        <template #cell(first_value_date)="data">
          {{ formatDate(data.item.first_value_date) }}
        </template>

        <template #cell(time_to_first_value_days)="data">
          {{ formatDays(data.item.time_to_first_value_days) }}
        </template>

        <template #cell(previous_avg_order_value)="data">
          {{ formatCurrency(data.item.previous_avg_order_value) }}
        </template>

        <template #cell(current_avg_order_value)="data">
          {{ formatCurrency(data.item.current_avg_order_value) }}
        </template>

        <template #cell(adoption_percent)="data">
          <span
              class="coverage-badge"
              :class="getCoverageClass(data.item.adoption_percent)"
          >
            {{ formatPercent(data.item.adoption_percent) }}
          </span>
        </template>

        <template #cell(engagement_percent)="data">
          <span
              class="coverage-badge"
              :class="getCoverageClass(data.item.engagement_percent)"
          >
            {{ formatPercent(data.item.engagement_percent) }}
          </span>
        </template>
      </b-table>
    </admin-panel>
  </b-container>
</template>

<script>
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminPanel from "../components/AdminPanel";
import moment from "moment";
import {API_ROUTES} from "../api";

const REPORT_CONFIGS = {
  wow_order_volume_change: {
    key: "wow_order_volume_change",
    label: "WoW Orders",
    description: "Order volume change vs the same period in the prior week.",
    apiRouteResolver: () => API_ROUTES.ADMIN_REPORT_WOW_ORDER_VOLUME_CHANGE,
    exportFileName: "wow_order_volume_change",
    pdfTitle: "WoW Order Volume Change",
    defaultSort: "severity",
    defaultSortDirection: "asc",
    sortableColumns: ["change_percent", "severity"],
    columns: [
      {key: "account_type", label: "Type", thClass: "border-top-0 sortable-column", tdClass: "align-middle"},
      {key: "account_name", label: "Name", thClass: "border-top-0 sortable-column", tdClass: "align-middle"},
      {
        key: "current_orders",
        label: "Current Period",
        thClass: "border-top-0 sortable-column",
        tdClass: "align-middle"
      },
      {
        key: "previous_orders",
        label: "Prior Period",
        thClass: "border-top-0 sortable-column",
        tdClass: "align-middle"
      },
      {key: "change_percent", label: "WoW Change %", thClass: "border-top-0 sortable-column", tdClass: "align-middle"},
      {key: "severity", label: "Status", thClass: "border-top-0 sortable-column", tdClass: "align-middle"}
    ]
  },

  order_source_mix: {
    key: "order_source_mix",
    label: "Order Sources",
    description: "Breakdown of order sources for the selected date range.",
    apiRouteResolver: () => API_ROUTES.ADMIN_REPORT_ORDER_SOURCE_MIX,
    exportFileName: "order_source_mix",
    pdfTitle: "Order Source Mix",
    defaultSort: null,
    defaultSortDirection: "desc",
    sortableColumns: ["percent_orders"],
    columns: [
      {key: "source_name", label: "Source", thClass: "border-top-0", tdClass: "align-middle"},
      {key: "orders_count", label: "Orders", thClass: "border-top-0", tdClass: "align-middle"},
      {key: "prior_period_orders", label: "Prior Period", thClass: "border-top-0", tdClass: "align-middle"},
      {key: "percent_orders", label: "% Orders", thClass: "border-top-0", tdClass: "align-middle"}
    ]
  },

  last_order_date: {
    key: "last_order_date",
    label: "Last Order",
    description: "Days since last order per buyer.",
    apiRouteResolver: () => API_ROUTES.ADMIN_REPORT_LAST_ORDER_DATE,
    exportFileName: "last_order_date",
    pdfTitle: "Last Order Date",
    defaultSort: "status_days",
    defaultSortDirection: "desc",
    sortableColumns: ["status_days"],
    columns: [
      {key: "buyer_name", label: "Buyer", thClass: "border-top-0", tdClass: "align-middle"},
      {key: "total_orders", label: "Orders", thClass: "border-top-0", tdClass: "align-middle"},
      {key: "status_days", label: "Days", thClass: "border-top-0", tdClass: "align-middle"},
      {key: "status_display", label: "Status", thClass: "border-top-0", tdClass: "align-middle"}
    ]
  },

  gl_listing_rate: {
    key: "gl_listing_rate",
    label: "Listing Rate",
    description: "Inventory coverage / active listing rate by seller.",
    apiRouteResolver: () => API_ROUTES.ADMIN_REPORT_SELLERS_INVENTORY_COVERAGE,
    exportFileName: "gl_listing_rate",
    pdfTitle: "GL Listing Rate",
    defaultSort: "coverage_percent",
    defaultSortDirection: "desc",
    sortableColumns: ["coverage_percent"],
    columns: [
      {key: "seller_name", label: "Seller", thClass: "border-top-0 sortable-column", tdClass: "align-middle"},
      {
        key: "active_listings",
        label: "Active Listings",
        thClass: "border-top-0 sortable-column",
        tdClass: "align-middle"
      },
      {
        key: "total_inventory",
        label: "Total Inventory",
        thClass: "border-top-0 sortable-column",
        tdClass: "align-middle"
      },
      {key: "coverage_percent", label: "Coverage %", thClass: "border-top-0 sortable-column", tdClass: "align-middle"}
    ]
  },

  time_to_first_value: {
    key: "time_to_first_value",
    label: "Time to Value",
    description: "Time from company approval to first buyer order.",
    apiRouteResolver: () => API_ROUTES.ADMIN_REPORT_TIME_TO_FIRST_VALUE,
    exportFileName: "time_to_first_value",
    pdfTitle: "Time to First Value",
    defaultSort: null,
    defaultSortDirection: "desc",
    sortableColumns: [],
    columns: [
      {key: "company_name", label: "Company Name", thClass: "border-top-0", tdClass: "align-middle"},
      {key: "approved_at", label: "Approved At", thClass: "border-top-0", tdClass: "align-middle"},
      {
        key: "days_before_first_order",
        label: "Days Before First Order",
        thClass: "border-top-0",
        tdClass: "align-middle"
      },
      {key: "status_display", label: "Status", thClass: "border-top-0", tdClass: "align-middle"}
    ]
  },

  multi_user_engagement: {
    key: "multi_user_engagement",
    label: "Team Engagement",
    description: "Shows whether multiple users from the same company are actively engaged.",
    apiRouteResolver: () => API_ROUTES.ADMIN_REPORT_MULTI_USER_ENGAGEMENT,
    exportFileName: "multi_user_engagement",
    pdfTitle: "Multi-User Engagement",
    defaultSort: null,
    defaultSortDirection: "desc",
    sortableColumns: [],
    columns: [
      {key: "company_name", label: "Company Name", thClass: "border-top-0", tdClass: "align-middle"},
      {key: "users_count", label: "Users Count", thClass: "border-top-0", tdClass: "align-middle"},
      {
        key: "active_users_last_30_days",
        label: "Active Users Last 30 Days",
        thClass: "border-top-0",
        tdClass: "align-middle"
      },
      {key: "status_display", label: "Status", thClass: "border-top-0", tdClass: "align-middle"}
    ]
  },

  feature_adoption_breadth: {
    key: "feature_adoption_breadth",
    label: "Feature Adoption",
    description: "Shows how broadly platform features are adopted.",
    apiRouteResolver: () => API_ROUTES.ADMIN_REPORT_FEATURE_ADOPTION_BREADTH,
    exportFileName: "feature_adoption_breadth",
    pdfTitle: "Feature Adoption Breadth",
    defaultSort: "adoption_percent",
    defaultSortDirection: "desc",
    sortableColumns: ["seller_name", "used_features", "available_features", "adoption_percent"],
    columns: [
      {key: "seller_name", label: "Seller", thClass: "border-top-0 sortable-column", tdClass: "align-middle"},
      {key: "used_features", label: "Used Features", thClass: "border-top-0 sortable-column", tdClass: "align-middle"},
      {
        key: "available_features",
        label: "Available Features",
        thClass: "border-top-0 sortable-column",
        tdClass: "align-middle"
      },
      {key: "adoption_percent", label: "Adoption %", thClass: "border-top-0 sortable-column", tdClass: "align-middle"}
    ]
  },

  order_value_trajectory: {
    key: "order_value_trajectory",
    label: "Order Value Trend",
    description: "Tracks value trend across the current and prior periods.",
    apiRouteResolver: () => API_ROUTES.ADMIN_REPORT_ORDER_VALUE_TRAJECTORY,
    exportFileName: "order_value_trajectory",
    pdfTitle: "Order Value Trajectory",
    defaultSort: null,
    defaultSortDirection: "desc",
    sortableColumns: [],
    columns: [
      {key: "company_name", label: "Company Name", thClass: "border-top-0", tdClass: "align-middle"},
      {key: "current_period_value", label: "Orders Value", thClass: "border-top-0", tdClass: "align-middle"},
      {key: "prior_period_value", label: "Orders Value prior period", thClass: "border-top-0", tdClass: "align-middle"},
      {key: "status_display", label: "Status", thClass: "border-top-0", tdClass: "align-middle"}
    ]
  },
};

const VISIBLE_REPORT_KEYS = [
  "wow_order_volume_change",
  "order_source_mix",
  "last_order_date",
  "gl_listing_rate",
  "time_to_first_value",
  "multi_user_engagement",
  "order_value_trajectory",
];

const TABS_WITHOUT_DATE_FILTERS = [
  "multi_user_engagement",
];

export default {
  name: "AdminReportPerformanceAlerts",
  components: {
    AdminPanel,
    AdminBreadcrumb,
    AdminPreloader
  },

  data() {
    return {
      loading: true,
      activeTab: this.getInitialTabFromRoute(),
      reportDataByTab: {},
      alertsByTab: {},
      globalFilters: {
        date_from: moment().subtract(7, "days").format("YYYY-MM-DD"),
        date_to: moment().format("YYYY-MM-DD")
      },
      filtersByTab: this.buildInitialFiltersByTab()
    };
  },

  computed: {
    pageTitle() {
      return "Performance & Alerts";
    },

    reportTabs() {
      return VISIBLE_REPORT_KEYS
          .filter((key) => REPORT_CONFIGS[key])
          .map((key) => ({
            key,
            label: REPORT_CONFIGS[key].label
          }));
    },

    currentReportConfig() {
      return REPORT_CONFIGS[this.activeTab] || {};
    },

    currentReportColumns() {
      return this.currentReportConfig.columns || [];
    },

    sortableHeaderColumns() {
      const sortableKeys = this.currentReportConfig.sortableColumns || [];
      return this.currentReportColumns.filter((column) => sortableKeys.includes(column.key));
    },

    currentAlerts() {
      const alerts = this.alertsByTab[this.activeTab];
      return Array.isArray(alerts) ? alerts : [];
    },

    wowAccountScopeOptions() {
      return [
        {value: "all", text: "All Accounts"},
        {value: "seller", text: "Sellers"},
        {value: "buyer", text: "Buyers"}
      ];
    },

    maxDate() {
      return moment().format("YYYY-MM-DD");
    },

    formattedStartDate() {
      return this.globalFilters.date_from ? moment(this.globalFilters.date_from).format("L") : "-";
    },

    formattedEndDate() {
      return this.globalFilters.date_to ? moment(this.globalFilters.date_to).format("L") : "-";
    },

    currentReportRows() {
      const rawRows = Array.isArray(this.reportDataByTab[this.activeTab])
          ? this.reportDataByTab[this.activeTab]
          : [];

      return rawRows.map((item) => this.normalizeRow(item, this.activeTab));
    },

    shouldShowGlobalDateFilters() {
      return !TABS_WITHOUT_DATE_FILTERS.includes(this.activeTab);
    },
  },

  methods: {
    buildInitialFiltersByTab() {
      const filters = {};

      Object.values(REPORT_CONFIGS).forEach((config) => {
        filters[config.key] = {
          sort: config.defaultSort || null,
          sort_direction: config.defaultSortDirection || "desc"
        };
      });

      filters.wow_order_volume_change = {
        ...filters.wow_order_volume_change,
        account_scope: "all",
        account_id: null,
        decline_threshold_percent: 20
      };

      filters.order_source_mix = {
        ...filters.order_source_mix,
        min_gl_share: 50,
      };

      return filters;
    },

    setActiveTab(tabKey) {
      if (!REPORT_CONFIGS[tabKey] || !VISIBLE_REPORT_KEYS.includes(tabKey)) {
        return;
      }

      if (this.activeTab === tabKey) {
        this.updateTabInRoute(tabKey);
        return;
      }

      this.activeTab = tabKey;
      this.updateTabInRoute(tabKey);
      this.applyDefaultDateRangeForTab(tabKey);
      this.loadReportData(tabKey, true);
    },

    onStartDateChanged(startDate) {
      if (moment(startDate).isAfter(moment(this.globalFilters.date_to))) {
        this.globalFilters.date_to = startDate;
      }

      this.loadReportData(this.activeTab, true);
    },

    onEndDateChanged(endDate) {
      if (moment(endDate).isBefore(moment(this.globalFilters.date_from))) {
        this.globalFilters.date_from = endDate;
      }

      this.loadReportData(this.activeTab, true);
    },

    onFiltersChanged() {
      this.loadReportData(this.activeTab, true);
    },

    getReportUrl(tabKey = this.activeTab) {
      const config = REPORT_CONFIGS[tabKey];

      if (!config || typeof config.apiRouteResolver !== "function") {
        return null;
      }

      try {
        return config.apiRouteResolver();
      } catch (e) {
        return null;
      }
    },

    getReportParams(tabKey = this.activeTab) {
      const params = {};

      if (!TABS_WITHOUT_DATE_FILTERS.includes(tabKey)) {
        params.start_date = this.globalFilters.date_from;
        params.end_date = this.globalFilters.date_to;
      }

      const tabFilters = this.filtersByTab[tabKey] || {};

      if (tabFilters.sort) {
        params.sort = tabFilters.sort;
      }

      if (tabFilters.sort_direction) {
        params.sort_direction = tabFilters.sort_direction;
      }

      if (tabKey === "wow_order_volume_change") {
        params.account_scope = tabFilters.account_scope || "all";
        params.account_id = tabFilters.account_id || null;
        params.decline_threshold_percent = Number(tabFilters.decline_threshold_percent ?? 20);
      }

      if (tabKey === "order_source_mix") {
        params.min_gl_share = Number(tabFilters.min_gl_share ?? 50);
      }

      return params;
    },

    loadReportData(tabKey = this.activeTab, force = false) {
      if (!force && Array.isArray(this.reportDataByTab[tabKey])) {
        return;
      }

      const reportUrl = this.getReportUrl(tabKey);

      if (!reportUrl) {
        this.$set(this.reportDataByTab, tabKey, []);
        this.$set(this.alertsByTab, tabKey, []);

        this.$bvToast.toast("Report endpoint is not configured yet.", {
          title: "Info",
          variant: "warning"
        });

        this.loading = false;
        return;
      }

      const params = this.getReportParams(tabKey);

      this.loading = true;

      this.$api.get(reportUrl, {params})
          .then(({data}) => {
            const reportData = Array.isArray(data?.report_data) ? data.report_data : [];
            const alerts = Array.isArray(data?.alerts) ? data.alerts : [];

            this.$set(this.reportDataByTab, tabKey, reportData);
            this.$set(this.alertsByTab, tabKey, alerts);
          })
          .catch(({response}) => {
            let message = "Failed to load report";

            if (response && response.status === 422) {
              message = "Some of your params were incorrect";
              this.resetFilters();
            }

            this.$set(this.reportDataByTab, tabKey, []);
            this.$set(this.alertsByTab, tabKey, []);

            this.$bvToast.toast(message, {
              title: "Error!",
              variant: "danger"
            });
          })
          .finally(() => {
            this.loading = false;
          });
    },

    reloadCurrentReport() {
      this.loadReportData(this.activeTab, true);
    },

    resetFilters() {
      this.applyDefaultDateRangeForTab(this.activeTab);
      this.filtersByTab = this.buildInitialFiltersByTab();
      this.loadReportData(this.activeTab, true);
    },

    isColumnSortable(columnKey) {
      return (this.currentReportConfig.sortableColumns || []).includes(columnKey);
    },

    toggleSort(column) {
      if (!this.isColumnSortable(column)) {
        return;
      }

      const tabFilters = this.filtersByTab[this.activeTab];

      if (tabFilters.sort === column) {
        tabFilters.sort_direction = tabFilters.sort_direction === "asc" ? "desc" : "asc";
      } else {
        tabFilters.sort = column;
        tabFilters.sort_direction = "desc";
      }

      this.loadReportData(this.activeTab, true);
    },

    sortIcon(column) {
      const tabFilters = this.filtersByTab[this.activeTab] || {};

      if (tabFilters.sort !== column) {
        return "↕";
      }

      return tabFilters.sort_direction === "asc" ? "↑" : "↓";
    },

    normalizeRow(item, tabKey) {
      const baseRow = {
        seller_id: item?.seller_id ?? null,
        seller_name: item?.seller_name ?? "Unknown"
      };

      switch (tabKey) {
        case "wow_order_volume_change":
          return {
            account_type: item?.account_type ?? "seller",
            account_id: item?.account_id ?? null,
            account_name: item?.account_name ?? item?.seller_name ?? item?.buyer_name ?? "Unknown",
            current_orders: Number(item?.current_orders ?? 0),
            previous_orders: Number(item?.previous_orders ?? 0),
            change_percent: item?.change_percent,
            severity: item?.severity ?? this.getWowSeverity(item?.change_percent, item?.decline_threshold_percent),
            decline_threshold_percent: Number(
                item?.decline_threshold_percent ??
                this.filtersByTab.wow_order_volume_change.decline_threshold_percent ??
                20
            )
          };

        case "gl_listing_rate":
          return {
            ...baseRow,
            active_listings: Number(item?.active_listings ?? 0),
            total_inventory: Number(item?.total_inventory ?? 0),
            coverage_percent: item?.coverage_percent
          };

        case "order_source_mix":
          return {
            source_id: item?.source_id ?? null,
            source_name: item?.source_name ?? "Unknown",
            source_code: item?.source_code ?? null,
            orders_count: Number(item?.orders_count ?? 0),
            prior_period_orders: Number(item?.prior_period_orders ?? 0),
            percent_orders: item?.percent_orders,
          };

        case "last_order_date":
          return {
            buyer_id: item?.buyer_id ?? null,
            buyer_name: item?.buyer_name ?? "Unknown",
            total_orders: Number(item?.total_orders ?? 0),
            last_order_date: item?.last_order_date ?? null,
            days_since_last_order: Number(item?.days_since_last_order ?? 0),
            status: item?.status ?? "healthy",
            status_days: item?.days_since_last_order,
            status_display: this.getLastOrderStatusLabel(
                item?.status,
                item?.days_since_last_order,
                item?.last_order_date
            ),
          };

        case "time_to_first_value":
          return {
            company_id: item?.company_id ?? null,
            company_name: item?.company_name ?? "Unknown",
            approved_at: item?.approved_at ?? null,
            first_order_date: item?.first_order_date ?? null,
            days_before_first_order: item?.days_before_first_order ?? null,
            status: item?.status ?? "healthy",
            status_display: this.getTimeToFirstValueStatusLabel(
                item?.status,
                item?.days_before_first_order
            ),
          };

        case "multi_user_engagement":
          return {
            company_id: item?.company_id ?? null,
            company_name: item?.company_name ?? "Unknown",
            users_count: Number(item?.users_count ?? 0),
            active_users_last_30_days: Number(item?.active_users_last_30_days ?? 0),
            status: item?.status ?? "healthy",
            status_display: this.getTeamEngagementStatusLabel(item?.status),
          };

        case "feature_adoption_breadth":
          return {
            ...baseRow,
            used_features: Number(item?.used_features ?? 0),
            available_features: Number(item?.available_features ?? 0),
            adoption_percent: item?.adoption_percent
          };

        case "order_value_trajectory":
          return {
            company_id: item?.company_id ?? null,
            company_name: item?.company_name ?? "Unknown",
            current_period_value: item?.current_period_value,
            prior_period_value: item?.prior_period_value,
            pre_prior_period_value: item?.pre_prior_period_value,
            status: item?.status ?? "healthy",
            status_display: this.getOrderValueTrendStatusLabel(item?.status),
          };

        default:
          return {
            ...baseRow,
            ...item
          };
      }
    },

    formatPercent(value) {
      if (value === null || typeof value === "undefined" || value === "") {
        return "-";
      }

      const numericValue = Number(value);

      if (Number.isNaN(numericValue)) {
        return "-";
      }

      return `${numericValue > 0 ? "+" : ""}${numericValue.toFixed(2)}%`;
    },

    formatPlainPercent(value) {
      if (value === null || typeof value === "undefined" || value === "") {
        return "-";
      }

      const numericValue = Number(value);

      if (Number.isNaN(numericValue)) {
        return "-";
      }

      return `${numericValue.toFixed(2)}%`;
    },

    formatCurrency(value) {
      if (value === null || typeof value === "undefined" || value === "") {
        return "-";
      }

      const numericValue = Number(value);

      if (Number.isNaN(numericValue)) {
        return "-";
      }

      return `$${numericValue.toFixed(2)}`;
    },

    formatDate(value) {
      if (!value) {
        return "-";
      }

      return moment(value).format("L");
    },

    formatDays(value) {
      if (value === null || typeof value === "undefined" || value === "") {
        return "-";
      }

      const numericValue = Number(value);

      if (Number.isNaN(numericValue)) {
        return "-";
      }

      return `${numericValue} day${numericValue === 1 ? "" : "s"}`;
    },

    getCoverageClass(value) {
      const numericValue = Number(value);

      if (value === null || typeof value === "undefined" || Number.isNaN(numericValue)) {
        return "coverage-badge--empty";
      }

      if (numericValue < 40) {
        return "coverage-badge--danger";
      }

      if (numericValue < 70) {
        return "coverage-badge--warning";
      }

      return "coverage-badge--success";
    },

    getWowSeverity(value, threshold = 20) {
      const numericValue = Number(value);
      const numericThreshold = Math.abs(Number(threshold ?? 20));

      if (value === null || typeof value === "undefined" || Number.isNaN(numericValue)) {
        return "unknown";
      }

      if (numericValue <= (numericThreshold * -1)) {
        return "critical";
      }

      if (numericValue < 0) {
        return "warning";
      }

      return "healthy";
    },

    getWowSeverityLabel(value) {
      const severity = typeof value === "string" ? value : this.getWowSeverity(value);

      switch (severity) {
        case "healthy":
          return "🟢 Healthy";
        case "warning":
          return "🟡 Warning";
        case "critical":
          return "🔴 Critical";
        default:
          return "-";
      }
    },

    getWowSeverityClass(value) {
      const severity = typeof value === "string" ? value : this.getWowSeverity(value);

      return {
        "wow-status-badge--healthy": severity === "healthy",
        "wow-status-badge--warning": severity === "warning",
        "wow-status-badge--critical": severity === "critical",
        "wow-status-badge--empty": severity === "unknown"
      };
    },

    getChangeClass(value, threshold = 20) {
      const numericValue = Number(value);
      const numericThreshold = Math.abs(Number(threshold ?? 20));

      if (value === null || typeof value === "undefined" || Number.isNaN(numericValue)) {
        return "trajectory-badge--empty";
      }

      if (numericValue <= (numericThreshold * -1)) {
        return "trajectory-badge--danger";
      }

      if (numericValue < 0) {
        return "trajectory-badge--warning";
      }

      return "trajectory-badge--success";
    },

    getAlertClass(alert) {
      const level = alert?.level || "info";

      return {
        "performance-alerts-alert--info": level === "info",
        "performance-alerts-alert--success": level === "success",
        "performance-alerts-alert--warning": level === "warning",
        "performance-alerts-alert--danger": level === "danger"
      };
    },

    formatAlertValue(alert) {
      if (!alert) {
        return "-";
      }

      if (typeof alert.value === "number" && String(alert.threshold || "").includes("%")) {
        return this.formatPercent(alert.value);
      }

      if (typeof alert.value === "number") {
        return alert.value;
      }

      return alert.value || "-";
    },

    escapeCsvValue(value) {
      const stringValue = String(value ?? "");
      return `"${stringValue.replace(/"/g, '""')}"`;
    },

    getCsvValue(row, key) {
      const value = row[key];

      if (key === "severity") {
        return this.getWowSeverityLabel(value);
      }

      if (key === "percent_orders") {
        return this.isGlShareBelowThreshold(row)
            ? `Critical (${this.formatPlainPercent(value)})`
            : this.formatPlainPercent(value);
      }

      if (key.includes("percent")) {
        return this.formatPercent(value);
      }

      if (key.includes("avg_order_value") || key.includes("period_value")) {
        return this.formatCurrency(value);
      }

      if (key.includes("date")) {
        return row[key] ? moment(row[key]).format("YYYY-MM-DD") : "";
      }

      if (key === "time_to_first_value_days" || key === "days_before_first_order") {
        return this.formatDays(value);
      }

      if (value === null || typeof value === "undefined") {
        return "";
      }

      return String(value);
    },

    async downloadPdf() {
      try {
        const payload = {
          report_key: this.activeTab,
          title: this.currentReportConfig.pdfTitle || this.currentReportConfig.label || "Report",
          description: this.currentReportConfig.description || "",
          date_from: this.shouldShowGlobalDateFilters ? this.formattedStartDate : "",
          date_to: this.shouldShowGlobalDateFilters ? this.formattedEndDate : "",
          filters: this.getPdfFilters(),
          columns: this.currentReportColumns.map((column) => ({
            key: column.key,
            label: column.label,
          })),
          rows: this.getPdfRows(),
        };

        const response = await this.$api.post(API_ROUTES.ADMIN_REPORT_PDF_EXPORT, payload, {
          responseType: "blob",
        });

        const blob = new Blob([response.data], {type: "application/pdf"});
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `${this.currentReportConfig.exportFileName || "report"}.pdf`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.$bvToast.toast("Failed to export PDF", {
          title: "Error!",
          variant: "danger",
        });
      }
    },

    getPdfFilters() {
      const filters = {};

      if (this.activeTab === "wow_order_volume_change") {
        filters.Accounts = this.wowAccountScopeOptions.find(
            (item) => item.value === this.filtersByTab.wow_order_volume_change.account_scope
        )?.text || "All Accounts";

        filters["Alert Threshold"] =
            `${this.filtersByTab.wow_order_volume_change.decline_threshold_percent ?? 20}%`;
      }

      if (this.activeTab === "order_source_mix") {
        filters["Min GL Share"] =
            `${this.filtersByTab.order_source_mix.min_gl_share ?? 50}%`;
      }

      return filters;
    },

    getPdfRows() {
      return this.currentReportRows.map((row) => {
        const result = {...row};

        Object.keys(result).forEach((key) => {
          if (key === "severity") {
            result[key] = this.getWowSeverityLabel(result[key]).replace(/[🟢🟡🔴]\s*/g, "");
          } else if (key === "status_display") {
            result[key] = String(result[key] || "").replace(/[🟢🟡🟠🔴]\s*/g, "");
          } else if (key === "percent_orders") {
            result[key] = this.isGlShareBelowThreshold(row)
                ? `Critical (${this.formatPlainPercent(result[key])})`
                : this.formatPlainPercent(result[key]);
          } else if (key.includes("percent")) {
            result[key] = this.formatPercent(result[key]);
          } else if (key.includes("avg_order_value") || key.includes("period_value")) {
            result[key] = this.formatCurrency(result[key]);
          } else if (key.includes("date")) {
            result[key] = this.formatDate(result[key]);
          } else if (key === "time_to_first_value_days" || key === "days_before_first_order") {
            result[key] = this.formatDays(result[key]);
          }
        });

        return result;
      });
    },

    isGlShareBelowThreshold(row) {
      if (!row || row.source_code !== "gl") {
        return false;
      }

      const threshold = Number(this.filtersByTab.order_source_mix?.min_gl_share ?? 50);
      const percentOrders = Number(row.percent_orders ?? 0);

      if (Number.isNaN(threshold) || Number.isNaN(percentOrders)) {
        return false;
      }

      return percentOrders < threshold;
    },

    getInitialTabFromRoute() {
      const routeTab = this.$route?.query?.tab;

      if (routeTab && REPORT_CONFIGS[routeTab] && VISIBLE_REPORT_KEYS.includes(routeTab)) {
        return routeTab;
      }

      return VISIBLE_REPORT_KEYS[0];
    },

    updateTabInRoute(tabKey) {
      if (!this.$route || !this.$router) {
        return;
      }

      if (this.$route.query?.tab === tabKey) {
        return;
      }

      this.$router.replace({
        query: {
          ...this.$route.query,
          tab: tabKey,
        },
      }).catch(() => {
      });
    },

    formatSimpleStatusLabel(status, healthyLabel = "Healthy", warningLabel = "Warning") {
      switch (status) {
        case "critical":
          return "🔴 Critical";
        case "warning":
          return `🟡 ${warningLabel}`;
        default:
          return `🟢 ${healthyLabel}`;
      }
    },

    getLastOrderStatusLabel(status, daysSinceLastOrder, lastOrderDate) {
      const formattedDate = lastOrderDate ? this.formatDate(lastOrderDate) : "-";
      const daysText = typeof daysSinceLastOrder !== "undefined" && daysSinceLastOrder !== null
          ? `${daysSinceLastOrder} day${Number(daysSinceLastOrder) === 1 ? "" : "s"}`
          : "-";

      switch (status) {
        case "critical":
          return `🔴 Critical — ${daysText} since last order (${formattedDate})`;
        case "escalation":
          return `🟠 Escalation — ${daysText} since last order (${formattedDate})`;
        case "outreach_recommended":
          return `🟡 Outreach recommended — ${daysText} since last order (${formattedDate})`;
        default:
          return `🟢 Healthy — ${daysText} since last order (${formattedDate})`;
      }
    },

    getTimeToFirstValueStatusLabel(status, daysBeforeFirstOrder) {
      const daysText = daysBeforeFirstOrder === null || typeof daysBeforeFirstOrder === "undefined"
          ? "No orders yet"
          : `${daysBeforeFirstOrder} day${Number(daysBeforeFirstOrder) === 1 ? "" : "s"}`;

      if (status === "critical") {
        return `🔴 Critical — ${daysText}`;
      }

      return `🟢 Healthy — ${daysText}`;
    },

    getTeamEngagementStatusLabel(status) {
      return this.formatSimpleStatusLabel(status);
    },

    getOrderValueTrendStatusLabel(status) {
      return this.formatSimpleStatusLabel(status);
    },

    applyDefaultDateRangeForTab(tabKey) {
      if (tabKey === "last_order_date") {
        this.globalFilters.date_from = moment().subtract(70, "days").format("YYYY-MM-DD");
        this.globalFilters.date_to = moment().format("YYYY-MM-DD");
        return;
      }

      this.globalFilters.date_from = moment().subtract(7, "days").format("YYYY-MM-DD");
      this.globalFilters.date_to = moment().format("YYYY-MM-DD");
    },
  },

  watch: {
    "$route.query.tab"(newTab) {
      if (!newTab || !REPORT_CONFIGS[newTab] || !VISIBLE_REPORT_KEYS.includes(newTab) || newTab === this.activeTab) {
        return;
      }

      this.activeTab = newTab;
      this.applyDefaultDateRangeForTab(newTab);

      if (!Array.isArray(this.reportDataByTab[newTab])) {
        this.loadReportData(newTab, true);
      }
    }
  },

  created() {
    this.applyDefaultDateRangeForTab(this.activeTab);
    this.loadReportData(this.activeTab, true);
  }
};
</script>

<style scoped>
.performance-alerts-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.performance-alerts-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.performance-alerts-tab-btn {
  white-space: nowrap;
  padding: 8px 8px;
}

.performance-alerts-alerts {
  display: grid;
  gap: 10px;
}

.performance-alerts-alert {
  border-radius: 10px;
  padding: 12px 14px;
  border: 1px solid transparent;
}

.performance-alerts-alert__title {
  font-weight: 700;
  margin-bottom: 4px;
}

.performance-alerts-alert__message {
  margin-bottom: 4px;
}

.performance-alerts-alert__meta {
  font-size: 13px;
  opacity: 0.9;
}

.performance-alerts-alert--info {
  background: #eef6ff;
  border-color: #b6daff;
  color: #084298;
}

.performance-alerts-alert--success {
  background: #eaf7ee;
  border-color: #badbcc;
  color: #0f5132;
}

.performance-alerts-alert--warning {
  background: #fff8e5;
  border-color: #ffecb5;
  color: #664d03;
}

.performance-alerts-alert--danger {
  background: #fdeaea;
  border-color: #f5c2c7;
  color: #842029;
}

.report-inline-select {
  width: 180px;
  vertical-align: middle;
}

.report-inline-input {
  width: 100px;
  vertical-align: middle;
}

.report-sort-head {
  cursor: pointer;
  user-select: none;
  color: inherit;
  text-decoration: none;
}

.report-sort-head:hover,
.report-sort-head:focus {
  text-decoration: none;
  color: inherit;
  outline: none;
}

.coverage-badge,
.trajectory-badge,
.wow-status-badge {
  display: inline-block;
  min-width: 110px;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 600;
  text-align: center;
}

.coverage-badge {
  min-width: 82px;
}

.coverage-badge--danger,
.trajectory-badge--danger,
.wow-status-badge--critical {
  color: #842029;
  background-color: #f8d7da;
}

.coverage-badge--warning,
.trajectory-badge--warning,
.wow-status-badge--warning {
  color: #664d03;
  background-color: #fff3cd;
}

.coverage-badge--success,
.trajectory-badge--success,
.wow-status-badge--healthy {
  color: #0f5132;
  background-color: #d1e7dd;
}

.coverage-badge--empty,
.trajectory-badge--empty,
.wow-status-badge--empty {
  color: #6c757d;
  background-color: #e9ecef;
}

.performance-alerts-header {
  display: flex;
  flex-direction: column;
}

.performance-alerts-header__top {
  width: 100%;
}

.performance-alerts-header__bottom {
  width: 100%;
}

.performance-alerts-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
</style>